import { VALIDATION_CONSTANTS } from "../config/api";
import type {
  FileValidationResult,
  UserInfo,
  UserInfoValidationResult,
  FormValidationError,
  DateUrgency,
  ConfidenceLevel,
} from "../types/notice";

// File validation
export const validateFile = (file: File | null): FileValidationResult => {
  if (!file) {
    return { valid: false, error: "Please select a file" };
  }

  // Check file type by extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = VALIDATION_CONSTANTS.ALLOWED_FILE_TYPES.some(
    (ext) => fileName.endsWith(ext),
  );

  if (!hasValidExtension) {
    return {
      valid: false,
      error: `Only PDF files are allowed. Supported formats: ${VALIDATION_CONSTANTS.ALLOWED_FILE_TYPES.join(", ")}`,
    };
  }

  // Check MIME type
  if (!VALIDATION_CONSTANTS.ALLOWED_MIME_TYPES.includes(file.type as any)) {
    return {
      valid: false,
      error: "Invalid file type. Please upload a valid PDF file.",
    };
  }

  // Check file size - minimum
  if (file.size < VALIDATION_CONSTANTS.MIN_FILE_SIZE) {
    return {
      valid: false,
      error: "File appears to be empty or corrupted",
    };
  }

  // Check file size - maximum
  if (file.size > VALIDATION_CONSTANTS.MAX_FILE_SIZE) {
    const maxSizeMB = Math.round(
      VALIDATION_CONSTANTS.MAX_FILE_SIZE / (1024 * 1024),
    );
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large (${fileSizeMB}MB). Maximum size is ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
};

// Email validation
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Name validation (allows letters, spaces, hyphens, apostrophes)
export const validateName = (name: string): boolean => {
  const nameRegex = /^[a-zA-Z\s\-']{1,50}$/;
  return nameRegex.test(name.trim());
};

// Company name validation (more permissive)
export const validateCompanyName = (name: string): boolean => {
  const companyRegex = /^[a-zA-Z0-9\s\-'.,&()]{1,100}$/;
  return companyRegex.test(name.trim());
};

// Phone number validation (US format)
export const validatePhoneNumber = (phone: string): boolean => {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, "");

  // Check if it's exactly 10 digits (US format without country code)
  // or 11 digits starting with 1 (US format with country code)
  if (digitsOnly.length === 10) {
    return /^[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(digitsOnly);
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    return /^1[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(digitsOnly);
  }

  return false;
};

// User info validation
export const validateUserInfo = (
  userInfo: Partial<UserInfo>,
): UserInfoValidationResult => {
  const errors: FormValidationError[] = [];

  // Email validation
  if (!userInfo.user_email?.trim()) {
    errors.push({ field: "user_email", message: "Email address is required" });
  } else if (!validateEmail(userInfo.user_email)) {
    errors.push({
      field: "user_email",
      message: "Please enter a valid email address",
    });
  }

  // First name validation
  if (!userInfo.first_name?.trim()) {
    errors.push({ field: "first_name", message: "First name is required" });
  } else if (!validateName(userInfo.first_name)) {
    errors.push({
      field: "first_name",
      message: "First name contains invalid characters",
    });
  }

  // Last name validation
  if (!userInfo.last_name?.trim()) {
    errors.push({ field: "last_name", message: "Last name is required" });
  } else if (!validateName(userInfo.last_name)) {
    errors.push({
      field: "last_name",
      message: "Last name contains invalid characters",
    });
  }

  // Company name validation
  if (!userInfo.company_name?.trim()) {
    errors.push({ field: "company_name", message: "Company name is required" });
  } else if (!validateCompanyName(userInfo.company_name)) {
    errors.push({
      field: "company_name",
      message: "Company name contains invalid characters",
    });
  }

  // Phone number validation
  if (!userInfo.phone_number?.trim()) {
    errors.push({ field: "phone_number", message: "Phone number is required" });
  } else if (!validatePhoneNumber(userInfo.phone_number)) {
    errors.push({
      field: "phone_number",
      message: "Please enter a valid US phone number",
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

// Date urgency classification
export const getDateUrgency = (dateString: string | null): DateUrgency => {
  if (!dateString) return "normal";

  // Parse YYYY-MM-DD as local date to avoid UTC timezone shift
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed in JS Date
  const now = new Date();
  const daysDiff = Math.ceil(
    (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysDiff <= VALIDATION_CONSTANTS.DATE_URGENCY_DAYS.URGENT) {
    return "urgent";
  }
  if (daysDiff <= VALIDATION_CONSTANTS.DATE_URGENCY_DAYS.WARNING) {
    return "warning";
  }
  if (daysDiff <= VALIDATION_CONSTANTS.DATE_URGENCY_DAYS.ATTENTION) {
    return "attention";
  }
  return "normal";
};

// Confidence level classification
export const getConfidenceLevel = (score: number): ConfidenceLevel => {
  if (score >= VALIDATION_CONSTANTS.CONFIDENCE_THRESHOLD.HIGH) {
    return "high";
  }
  if (score >= VALIDATION_CONSTANTS.CONFIDENCE_THRESHOLD.MEDIUM) {
    return "medium";
  }
  return "low";
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Format date for display
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return "Not specified";

  // Parse YYYY-MM-DD as local date to avoid UTC timezone shift
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed in JS Date
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Format currency for display
export const formatCurrency = (amount: number | null): string => {
  if (amount === null || amount === undefined) return "Not specified";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

// Sanitize user input to prevent XSS
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>]/g, "") // Remove potential HTML tags
    .substring(0, 500); // Limit length
};

// Get urgency color class for Tailwind
export const getUrgencyColorClass = (urgency: DateUrgency): string => {
  const colorMap = {
    urgent: "text-red-600 bg-red-50 border-red-200",
    warning: "text-orange-600 bg-orange-50 border-orange-200",
    attention: "text-emerald-600 bg-emerald-50 border-emerald-200",
    normal: "text-gray-600 bg-gray-50 border-gray-200",
  };

  return colorMap[urgency];
};

// Get confidence color class for Tailwind
export const getConfidenceColorClass = (level: ConfidenceLevel): string => {
  const colorMap = {
    high: "text-green-600 bg-green-50",
    medium: "text-yellow-600 bg-yellow-50",
    low: "text-red-600 bg-red-50",
  };

  return colorMap[level];
};

// Format phone number for display
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "Not provided";

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, "");

  // Format as (XXX) XXX-XXXX for 10 digits
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }

  // Format as +1 (XXX) XXX-XXXX for 11 digits starting with 1
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    const phoneDigits = digitsOnly.slice(1);
    return `+1 (${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
  }

  // Return original if it doesn't match expected formats
  return phone;
};
